import { useEffect, useState } from "react";
import { teacherApi, type RequestLesson } from "@/services/api/teacher";
import { lessonActionError, type LessonChoice } from "./talkRadarReadFormat";

export type DayAsk = {
  ask: string;
  items: { lesson_request_id: string; response: "accept" | "decline" }[];
};

export function useTalkRadarRespond(
  requestId: string | null,
  setTeacherLessons: (lessons: RequestLesson[]) => void
) {
  const [pending, setPending] = useState<{
    lessonId: string;
    choice: LessonChoice;
  } | null>(null);
  const [dayAsk, setDayAsk] = useState<DayAsk | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    setPending(null);
    setDayAsk(null);
    setActionError(null);
  }, [requestId]);

  async function confirmChoice() {
    if (!pending || !requestId) return;
    const choice = pending;
    setActionError(null);
    try {
      const result = await teacherApi.respondToLessons(requestId, [
        { lesson_request_id: choice.lessonId, response: choice.choice },
      ]);
      if (!result.success) {
        setActionError(
          result.message || "Einige Lektionen konnten nicht verarbeitet werden"
        );
        return;
      }
      const next = await teacherApi.getRequestLessons(requestId);
      setTeacherLessons(next.lessons || []);
      setPending(null);
    } catch (err) {
      setActionError(
        lessonActionError(err, "Fehler beim Verarbeiten der Lektionen")
      );
    }
  }

  async function confirmDay() {
    if (!dayAsk || !requestId || dayAsk.items.length === 0) return;
    const asked = dayAsk;
    setActionError(null);
    try {
      const result = await teacherApi.respondToLessons(requestId, asked.items);
      if (!result.success) {
        setActionError(
          result.message || "Einige Lektionen konnten nicht verarbeitet werden"
        );
        return;
      }
      const next = await teacherApi.getRequestLessons(requestId);
      setTeacherLessons(next.lessons || []);
      setDayAsk(null);
    } catch (err) {
      setActionError(
        lessonActionError(err, "Fehler beim Verarbeiten der Lektionen")
      );
    }
  }

  return {
    pending,
    dayAsk,
    actionError,
    choose: (lessonId: string, choice: LessonChoice) => {
      setDayAsk(null);
      setPending({ lessonId, choice });
    },
    cancel: () => setPending(null),
    confirmChoice,
    askDay: (ask: DayAsk) => {
      setPending(null);
      setDayAsk(ask);
    },
    cancelDay: () => setDayAsk(null),
    confirmDay,
  };
}
